/* eslint-disable no-empty */
import { put, call, takeLatest, fork, select } from 'redux-saga/effects';
import SagaUtils from 'Utils/SagaUtils';
import FileSaver from 'file-saver';
import { UsageActions } from './actions';
import ApiService from '../../../Services/ApiService';
import { displayNotification } from '../../../Utils/GenericUtils';
import { UsageSelectors } from './selectors';
import AppConfig from '../../../Config/AppConfig';
import { getDefaultStartDate, getDefaultEndDate, today } from './utils';

export function* getUsageData(action) {
  const timeFilter = {
    start_date: getDefaultStartDate(),
    end_date: getDefaultEndDate(),
  };
  const timeRange = action.payload || timeFilter || '';
  try {
    const res = yield call(ApiService.getUsageData, timeRange);
    yield put(UsageActions.fetchUsageDataSuccess(res));
  } catch (e) {
    yield put(UsageActions.fetchUsageDataFailed(e.response.data));
    displayNotification('error', 'Error', 'Error getting usage data');
  }
}

export function* getUsageAnalytics(action) {
  try {
    const timeRange = action.payload || '';
    const res = yield call(ApiService.getUsageAnalytics, timeRange);
    yield put(UsageActions.fetchUsageAnalyticsSuccess(res));
  } catch (e) {
    yield put(UsageActions.fetchUsageAnalyticsFailed(e.response));
    displayNotification('error', 'Error', 'Error getting usage analytics');
  }
}

export function* requestUsageExport(action) {
  const selectFilter = yield select(UsageSelectors.timeFilterSelector);
  const timeFilter = {
    start_date: action.payload.start_date || selectFilter.startDate || getDefaultStartDate(),
    end_date: action.payload.end_date || selectFilter.endDate || getDefaultEndDate(),
  };
  try {
    const response = yield call(ApiService.requestUsageExport, timeFilter);
    yield put(UsageActions.exportUsageListInProgress({ requestId: response.data.request_id }));
  } catch (e) {
    yield put(UsageActions.exportUsageListFailed(e.response.data));
    displayNotification('error', 'Error', 'Error exporting usage list');
  }
}

export function* getUsageExportStatus() {
  const requestId = yield select(UsageSelectors.exportRequestIdSelector);
  const isReady = yield select(UsageSelectors.isExportRequestReadySelector);
  const shouldSkipStatusCheck = isReady || !requestId;
  if (shouldSkipStatusCheck) {
    return;
  }
  try {
    const response = yield call(ApiService.getUsageExportStatus, requestId);
    yield put(UsageActions.exportUsageListReady({ isReady: response.data.is_ready }));
  } catch (e) {
    yield put(UsageActions.exportUsageListFailed(e.response.data));
    displayNotification('error', 'Error', 'Error exporting usage list');
  }
}

export function* exportUsageList() {
  const requestId = yield select(UsageSelectors.exportRequestIdSelector);
  const isReady = yield select(UsageSelectors.isExportRequestReadySelector);
  const canDownload = requestId && isReady;
  if (!canDownload) {
    return;
  }
  try {
    const response = yield call(ApiService.exportUsage, requestId);
    const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8' });
    const filename = `${'users_usage'}_${today()}.csv`;
    FileSaver.saveAs(blob, filename);
    yield put(UsageActions.exportUsageListSucceeded());
  } catch (e) {
    yield put(UsageActions.exportUsageListFailed(e.response.data));
    displayNotification('error', 'Error', 'Error exporting usage list');
  }
}

function* onLoad() {
  if (AppConfig.USAGE_ANALYTICS_ENABLED) {
    yield put(UsageActions.fetchUsageData());
  }
}

export function* watchUsage() {
  yield SagaUtils.allIfOrgAdmin([
    fork(onLoad),
    takeLatest([UsageActions.fetchUsageData], getUsageData),
    takeLatest([UsageActions.exportUsageList], requestUsageExport),
    takeLatest([UsageActions.exportUsageListInProgress], getUsageExportStatus),
    takeLatest([UsageActions.exportUsageListReady], exportUsageList),
    takeLatest([UsageActions.fetchUsageAnalytics], getUsageAnalytics),
  ]);
}
